<template>
  <div class="imageset-popup">
    <div class="imageset-popup__header">
      <div class="imageset-popup__store-name">
        {{ store.store_name }}
      </div>
      <div class="imageset-popup__date">
        {{ maskableTime(store.uploaded_at) }}
      </div>
    </div>
    <div class="imageset-popup__carousel">
      <el-button :disabled="imageOrder < 2"
                 class="imageset-popup__carousel-button imageset-popup__carousel-button--prev"
                 icon="el-icon-arrow-left"
                 @click="previousImage()" />
      <div class="imageset-popup__carousel-image-wrapper">
        <vue-easy-lightbox :visible="lightboxVisibility"
                           :imgs="imagesetImages"
                           :index="lightboxImageIndex"
                           @hide="closeLightbox"
                           @on-index-change="changeImageIndex">
          <template v-slot:toolbar="{ toolbarMethods }">
            <div class="lightbox-button-wrapper">
              <el-button icon="el-icon-zoom-in" @click="toolbarMethods.zoomIn" />
              <el-button icon="el-icon-zoom-out" @click="toolbarMethods.zoomOut" />
              <el-button icon="el-icon-full-screen" @click="toolbarMethods.resize" />
              <el-button icon="el-icon-refresh-left" @click="toolbarMethods.rotateLeft" />
              <el-button icon="el-icon-refresh-right" @click="toolbarMethods.rotateRight" />
              <el-button icon="el-icon-download" @click="imageDownloader(downloadedImage)" />
            </div>
          </template>
        </vue-easy-lightbox>
        <img :src="value.file_urls.original"
             class="imageset-popup__carousel-image"
             @click="openLightbox"
             @error="onImgError">
        <div class="imageset-popup__carousel-layout">
          {{ $t("Click to zoom") }}
        </div>
      </div>
      <el-button :disabled="imageOrder >= imageCount"
                 class="imageset-popup__carousel-button imageset-popup__carousel-button--next"
                 icon="el-icon-arrow-right"
                 @click="nextImage()" />
    </div>
    <div class="imageset-popup__footer">
      <div class="imageset-popup__info-left">
        <div class="imageset-popup__info-grow">
          <div>
            <span class="imageset-popup__info-title">
              {{ imageset.activity_category_name }}
            </span>
            <span class="imageset-popup__info-subtitle">
              {{ imageset.image_set_type_name }}
            </span>
          </div>
          <div>
            <el-button
              v-if="imageset.report_status !== 'discarded' && imageset.mosaic"
              class="imageset-popup__info-button"
              type="text"
              @click="$emit('openPanorama')">
              <svg style="width: 24px; height: 24px;" viewBox="0 0 24 24">
                <!-- eslint-disable-next-line -->
              <path fill="currentColor" d="M21 4C20.4 4 17.4 5.5 12 5.5C6.7 5.5 3.5 4 3 4C2.5 4 2 4.4 2 5V19C2 19.6 2.5 20 3 20C3.6 20 6.5 18.5 12 18.5C17.4 18.5 20.4 20 21 20C21.5 20 22 19.6 22 19V5C22 4.4 21.5 4 21 4M20 17.6C18 17 15.4 16.5 12 16.5C8.6 16.5 6 17 4 17.6V6.4C6.6 7.1 9.3 7.5 12 7.5C15.4 7.5 18 7 20 6.4V17.6M9.2 11L5.5 15.4C7.5 15.1 9.7 15 12 15C14.3 15 16.5 15.1 18.5 15.4L14 10L11.2 13.4L9.2 11Z" />
              </svg>
            </el-button>
            <el-button
              v-if="isReportInspectorActive"
              class="imageset-popup__info-button"
              @click="$emit('reqReportInspector')">
              <img
                src="/statics/modern-imageset-icons/report-inspector.svg"
                alt="report-inspector">
            </el-button>
          </div>
        </div>
        <DownloadButton :downloading="imageDownloading" @click="downloadImageset">
          {{ $t("Imageset") }}
        </DownloadButton>
      </div>
      <div class="imageset-popup__info-right">
        <div class="imageset-popup__counter">
          <span>{{ imageOrder }}</span> {{ `/ ${imageCount}` }}
        </div>
        <a class="imageset-popup__download"
           :href="value.file_urls.original"
           download>
          <i class="mdi mdi-download" />
          <span>{{ $t("Image") }}
          </span>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import imageDownloader from '@/helpers/imageDownloader';
import ImageDownloadMixin from './ImageDownloadMixin';

export default {
  name: 'ImagesetPopup',
  mixins: [ImageDownloadMixin],
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    store: {
      type: Object,
      default: () => ({})
    },
    isReportInspectorActive: {
      type: Boolean,
      default: false
    },
    imageset: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      imagesetDownloading: false,
      lightboxVisibility: false,
      lightboxImageIndex: 0,
      downloadedImage: ''
    };
  },
  computed: {
    imageOrder() {
      return this.imageset.images.findIndex(i => this.value.id === i.id) + 1;
    },
    imageCount() {
      return this.imageset.images.length;
    },
    imagesetImages() {
      return this.imageset.images.map(i => i.file_urls.original);
    }
  },
  methods: {
    previousImage() {
      const previousImage = this.imageset.images[this.imageOrder - 2];

      if (previousImage) this.$emit('input', previousImage);
    },
    nextImage() {
      const nextImage = this.imageset.images[this.imageOrder];

      if (nextImage) this.$emit('input', nextImage);
    },
    downloadImageset() {
      const mappedImages = this.imageset.images.map(i => ({ ...i, activity_category_name: this.imageset.activity_category_name, image_set_type_name: this.imageset.image_set_type_name }));

      this.downloadImages(mappedImages, `${this.imageset.activity_category_name}_${this.imageset.image_set_type_name}`, this.imageNamePrefix);
    },
    imageNamePrefix(image) {
      return `${image.activity_category_name}_${image.image_set_type_name}_${image.position}_${image.id}`;
    },
    openLightbox() {
      this.lightboxVisibility = true;
      this.lightboxImageIndex = this.imageOrder - 1;
      this.downloadedImage = this.imagesetImages[this.lightboxImageIndex];
    },
    closeLightbox() {
      this.lightboxVisibility = false;
    },
    changeImageIndex(index) {
      this.downloadedImage = this.imagesetImages[index - 1];
    },
    imageDownloader
  }
};
</script>

<style lang="scss">
.imageset-popup {
  &__header {
    padding: 0 100px;
    margin-bottom: 15px;
  }

  &__store-name {
    margin-bottom: 5px;
    font-size: 15px;
    font-weight: 700;
    color: $blackLacquer;
  }

  &__date {
    font-size: 15px;
  }

  &__carousel {
    display: flex;
    align-items: center;
    margin-bottom: 30px;
  }

  &__carousel-button {
    width: 100px;
    font-size: 80px;
    font-weight: 300;
    border: none;
    outline: none;
  }

  &__carousel-image-wrapper {
    position: relative;
    display: flex;
    flex-grow: 1;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
      .imageset-popup__carousel-layout {
        visibility: visible;
      }
    }
  }

  &__carousel-image {
    width: auto;
    max-width: 100%;
    max-height: 60vh;
  }

  &__carousel-layout {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-weight: 600;
    color: #bbb;
    pointer-events: none;
    visibility: hidden;
    background: rgba(0, 0, 0, 0.5);
  }

  &__footer {
    display: flex;
    padding: 0 100px;
  }

  &__info-left {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  &__info-grow {
    flex-grow: 1;
  }

  &__info-title {
    display: inline-block;
    padding: 5px 10px;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    background: $greyShingle;
    border-radius: 50px;
  }

  &__info-subtitle {
    display: inline-block;
    padding: 5px 10px;
    font-size: 15px;
    font-weight: 600;
    color: $greyShingle;
  }

  &__info-button {
    padding: 5px;
    background: none !important;
    border: none;
    border-radius: 0;
    outline: 0;

    &:nth-child(2) {
      padding-left: 10px;
    }

    img {
      width: 27px !important;
      height: auto;
    }

    svg {
      width: 22px !important;
      height: 25px !important;
      color: #aaa !important;
    }
  }

  &__info-right {
    display: flex;
    flex-direction: column;
    padding: 0 20px;
    border-left: 1px solid $hydrargyrum;
  }

  &__counter {
    flex-grow: 1;
    margin-bottom: 25px;
    font-size: 36px;
    font-weight: 600;
    color: $hydrargyrum;

    span {
      color: $tooGold;
    }
  }

  &__download {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: $tooGold;
    cursor: pointer;
    background: none;
    border: none;
    outline: none;

    i {
      padding-right: 5px;
      font-size: 30px;
    }
  }

  @media (max-width: 640px) {
    &__header {
      padding: 0 15px;
    }

    &__carousel {
      position: relative;

      &-button {
        position: absolute;
        width: 40px;
        height: 40px;
        font-size: 20px;

        &--next {
          top: 0;
          right: 0;
          bottom: 0;
          margin: auto;
        }

        &--prev {
          top: 0;
          bottom: 0;
          left: 0;
          margin: auto;
        }
      }
    }

    &__footer {
      padding: 0 15px;
    }
  }
}

</style>
